import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { clearStoredSession } from '@/lib/session/sessionManager';
import { toast as uiToast } from '@/components/ui/toast';
import type { ApiError, HttpStatusCode } from '@/types/common/errors';

// Simple cooldown to avoid spamming multiple error toasts
const ERROR_TOAST_COOLDOWN_MS = 8000;
let LAST_ERROR_TOAST_AT = 0;

export const createBaseQuery = (baseUrl: string, authHeaderName: string = 'authToken') => {
  return fetchBaseQuery({
    baseUrl,
    credentials: 'include',

    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem('authToken');
      if (token) {
        headers.set(authHeaderName, token);
      }
      return headers;
    },

    validateStatus: (response, result) => {
      // HTTP error status codes
      if (response.status >= 400) {
        return false; // Triggers transformErrorResponse
      }

      // Check for server-side error indicators in successful HTTP responses
      if (result && typeof result === 'object') {
        // Many APIs return errors with 200 status but error indicators in body
        if (result.statusCode && result.statusCode >= 400) {
          return false; // Triggers transformErrorResponse
        }
        if (result.error || result.success === false) {
          return false; // Triggers transformErrorResponse
        }
      }

      return true; // Successful response
    },
  });
};

export const createApiErrorTransformer = (tagName: string, showToast: boolean = true) => {
  return (response: any): ApiError => {
    if (import.meta.env?.DEV) {
      console.log(`>>>Creating>>> Error Transformer - Tag: ${tagName} - Toast: ${showToast}`);
    }
    return transformApiError(response, tagName, showToast);
  };
};

export const transformApiError = (
  response: any,
  tagName?: string,
  showToast: boolean = true,
): ApiError => {
  // Handle different response structures
  let status: number | HttpStatusCode;
  let statusText: string;
  let errorMessage: string;

  if (response?.status) {
    // Standard HTTP error response
    status = response.status;
    statusText = response.statusText || 'Unknown Error';
    errorMessage =
      response?.data?.message ||
      response?.data?.error ||
      response?.message ||
      response?.data ||
      `HTTP ${status} Error`;
  } else if (response?.statusCode) {
    // API response with statusCode field (even in 200 responses)
    status = response.statusCode;
    statusText = response.statusText || 'API Error';
    errorMessage = response.message || response.error || `API Error ${status}`;
  } else {
    // Fallback for unknown error formats
    status = 500;
    statusText = 'Unknown Error';
    errorMessage = response?.message || response?.error || 'An unexpected error occurred';
  }

  const error: ApiError = {
    status,
    statusText,
    data: getErrorMessage(status as HttpStatusCode, errorMessage),
    message: getErrorMessage(status as HttpStatusCode, errorMessage),
    context: {
      tagName,
      showToast,
    },
  };

  if (status === 401 || status === 403) {
    if (import.meta.env?.DEV)
      console.log(`>>>if>>> status === 401 || status === 403`, { tagName, status, showToast });
    clearSession();
    setTimeout(() => {
      navigateToLogin();
    }, 1500);
  } else {
    if (import.meta.env?.DEV)
      console.log(`>>>else>>> non-auth error branch (status not 401/403)`, {
        tagName,
        status,
        showToast,
      });
    // Show toast notification if enabled
    if (showToast) ShowErrorToast(error);
  }
  if (import.meta.env?.DEV) console.log('>>>API Error>>> transformed:', error);
  return error;
};

export const handleApiSuccess = (
  tagName: string,
  message: string = 'Operation completed successfully.',
  showToast: boolean = true,
  status: number = 200,
): void => {
  if (!showToast) return;

  try {
    if (import.meta.env?.DEV) console.log('>>>API Success>>>', { tagName, message, status });
    uiToast({
      title: 'Success',
      description: message,
      variant: 'success',
      open: true,
    });
  } catch (e) {
    console.error('Error dispatching success toast:', e);
  }
};

// Helper function to extract message from customMessage (handles stringified JSON objects)
const extractCustomMessage = (msg?: any): string | undefined => {
  if (!msg) return undefined;

  // If it's a stringified JSON object, try to parse and extract the message
  if (typeof msg === 'string') {
    if (msg.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(msg);
        // Try common message field names in order of priority
        return parsed?.message || parsed?.error || parsed?.data?.message || undefined;
      } catch {
        // Not valid JSON, return as-is
        return msg;
      }
    }
    return msg;
  }

  if (typeof msg === 'object') {
    return msg?.message || msg?.error || msg?.data?.message || undefined;
  }

  return String(msg);
};

const getErrorMessage = (status: HttpStatusCode, customMessage?: any): string => {
  const errorMessages: Record<HttpStatusCode, string> = {
    400: 'Invalid request. Please check your input and try again.',
    404: 'The requested resource was not found.',
    408: 'Request timed out. Please check your connection and try again.',
    409: 'There was a conflict with your request. Please refresh and try again.',
    422: 'The submitted data could not be processed. Please verify your input.',
    429: 'Too many requests. Please wait a moment before trying again.',
    500: 'Internal server error. Please try again later.',
    502: 'Service temporarily unavailable. Please try again in a few moments.',
    503: 'Service is currently under maintenance. Please try again later.',
    504: 'Request timeout. Please check your connection and try again.',
    FETCH_ERROR: 'Network error. Please check your internet connection and try again.',
    PARSING_ERROR: 'Received an invalid response from the server. Please try again.',
    TIMEOUT_ERROR: 'The request timed out. Please retry in a moment.',
    CUSTOM_ERROR: 'An unexpected error occurred while processing your request.',
  };

  const mappedMsg = errorMessages[status];
  const extractedMessage = extractCustomMessage(customMessage);

  // If backend provided a custom message, prioritize it over generic mapped message
  if (
    extractedMessage &&
    typeof extractedMessage === 'string' &&
    extractedMessage.trim().length > 0
  ) {
    return extractedMessage;
  }

  return mappedMsg || 'An unexpected error occurred';
};

const clearSession = (): void => {
  try {
    // Use centralized session clearing
    clearStoredSession();
  } catch (error) {
    console.error('Error clearing session:', error);
  }
};

const navigateToLogin = (): void => {
  try {
    window.location.href = '/login';
  } catch (error) {
    console.error('Error navigating to login:', error);
    window.location.reload();
  }
};

const ShowErrorToast = (error: ApiError): void => {
  try {
    const now = Date.now();
    if (now - LAST_ERROR_TOAST_AT < ERROR_TOAST_COOLDOWN_MS) return;

    const message = (error.data || error.message || 'An unexpected error occurred').toString();
    uiToast({ title: 'Error', description: message, variant: 'destructive', open: true });
    LAST_ERROR_TOAST_AT = now;
  } catch (e) {
    console.error('Error showing error toast:', e);
  }
};
